import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Banknote, CreditCard, Gift, Landmark, MoreHorizontal, Wallet } from 'lucide-react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  formatAmount,
  formatRate,
  parseAmountToCents,
  type Order,
  type OrderItem,
  type PaymentMethod,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCurrency } from '../lib/currency';
import { useSettings } from '../hooks/queries';
import { CloseOrderDialog } from './InvoiceBuyerDialog';
import { useToast } from '../hooks/useToast';
import { MoveItemDialog } from './MoveItemDialog';
import { SendToPrinterButton } from './ReceiptSheet';
import { TipField, useTipInput } from './GuestMix';
import { Badge, Button, Field, Input, Modal } from './ui';

const METHOD_ICON: Record<PaymentMethod, typeof Banknote> = {
  CASH: Banknote,
  CREDIT_CARD: CreditCard,
  DEBIT_CARD: Wallet,
  BANK_TRANSFER: Landmark,
  OTHER: MoreHorizontal,
};

/** Round up to the next note a guest is likely to hand over. */
function cashSuggestions(balanceCents: number): number[] {
  const notes = [500, 1_000, 2_000, 5_000, 10_000];
  const suggestions = new Set<number>([balanceCents]);
  for (const note of notes) {
    const rounded = Math.ceil(balanceCents / note) * note;
    if (rounded > balanceCents) suggestions.add(rounded);
  }
  return [...suggestions].sort((a, b) => a - b).slice(0, 5);
}

/** The method grid plus cash-tendered/reference fields, shared by both payment modes. */
function PaymentMethodFields({
  method,
  onMethod,
  amountCents,
  tenderedText,
  onTenderedChange,
  reference,
  onReferenceChange,
}: {
  method: PaymentMethod;
  onMethod: (method: PaymentMethod) => void;
  amountCents: number;
  tenderedText: string;
  onTenderedChange: (value: string) => void;
  reference: string;
  onReferenceChange: (value: string) => void;
}) {
  const { money, symbol, decimals } = useCurrency();
  const tenderedCents = useMemo(() => {
    if (!tenderedText) return null;
    try {
      return parseAmountToCents(tenderedText);
    } catch {
      return NaN;
    }
  }, [tenderedText]);
  const tenderValid =
    tenderedCents === null || (Number.isFinite(tenderedCents) && tenderedCents >= amountCents);
  const changeCents =
    tenderedCents !== null && Number.isFinite(tenderedCents) && tenderedCents >= amountCents
      ? tenderedCents - amountCents
      : 0;

  return (
    <>
      <div>
        <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-faint">
          Method
        </span>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {PAYMENT_METHODS.map((candidate) => {
            const Icon = METHOD_ICON[candidate];
            return (
              <button
                key={candidate}
                type="button"
                onClick={() => onMethod(candidate)}
                className={clsx(
                  'flex h-16 flex-col items-center justify-center gap-1 rounded-lg border text-xs font-medium transition',
                  method === candidate
                    ? 'border-accent bg-accent/12 text-accent-strong'
                    : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                )}
              >
                <Icon size={20} />
                {PAYMENT_METHOD_LABELS[candidate]}
              </button>
            );
          })}
        </div>
      </div>

      {method === 'CASH' ? (
        <div>
          <Field
            label="Cash tendered"
            error={!tenderValid ? 'Tendered must cover the amount charged' : undefined}
          >
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">{symbol}</span>
              <Input
                inputMode="decimal"
                className="pl-8 text-lg"
                placeholder={formatAmount(amountCents || 0, decimals)}
                value={tenderedText}
                onChange={(event) => onTenderedChange(event.target.value)}
              />
            </div>
          </Field>
          <div className="mt-2 flex flex-wrap gap-2">
            {cashSuggestions(amountCents).map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => onTenderedChange(formatAmount(suggestion, decimals))}
                className="h-10 rounded-lg border border-line px-3 text-sm text-ink-soft transition hover:border-accent/50 hover:text-accent-strong"
              >
                {money(suggestion)}
              </button>
            ))}
          </div>
          {changeCents > 0 ? (
            <div className="mt-3 flex items-center justify-between rounded-lg border border-positive/40 bg-positive/10 px-4 py-3">
              <span className="text-sm text-ink-soft">Change due</span>
              <span className="tnum font-display text-2xl text-positive">{money(changeCents)}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <Field label="Reference (optional)" hint="Terminal approval code, transfer reference…">
          <Input value={reference} onChange={(event) => onReferenceChange(event.target.value)} />
        </Field>
      )}
    </>
  );
}

/** One line in the split-by-item checklist. */
function SplitItemRow({
  item,
  quantity,
  onChange,
  onMove,
}: {
  item: OrderItem;
  quantity: number;
  onChange: (next: number) => void;
  onMove: () => void;
}) {
  const { money } = useCurrency();
  const remaining = item.quantity - item.paidQuantity;
  const fullyPaid = remaining <= 0;

  return (
    <div
      className={clsx(
        'flex items-center gap-3 rounded-lg border px-3 py-2.5',
        fullyPaid ? 'border-line/60 bg-transparent opacity-60' : 'border-line bg-canvas',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm text-ink">{item.nameSnapshot}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-faint">
          <span>{money(item.lineTotalCents / item.quantity)} each</span>
          {fullyPaid ? <Badge tone="positive">Paid</Badge> : item.paidQuantity > 0 ? (
            <Badge tone="caution">{item.paidQuantity}/{item.quantity} paid</Badge>
          ) : null}
          {!fullyPaid ? (
            <button
              type="button"
              onClick={onMove}
              className="text-ink-faint underline-offset-2 transition hover:text-accent hover:underline"
            >
              Move to another table/bill
            </button>
          ) : null}
        </div>
      </div>
      {fullyPaid ? null : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Fewer"
            onClick={() => onChange(Math.max(0, quantity - 1))}
            className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
          >
            −
          </button>
          <span className="tnum w-5 text-center text-sm text-ink">{quantity}</span>
          <button
            type="button"
            aria-label="More"
            onClick={() => onChange(Math.min(remaining, quantity + 1))}
            className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}

export function PaymentDialog({
  order,
  open,
  onClose,
  onSettled,
}: {
  order: Order;
  open: boolean;
  onClose: () => void;
  /** Called once the order is fully paid. */
  onSettled: (order: Order) => void;
}) {
  const { money, symbol, decimals } = useCurrency();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'full' | 'split'>('full');
  const [method, setMethod] = useState<PaymentMethod>('CREDIT_CARD');
  const [amountText, setAmountText] = useState('');
  const [tenderedText, setTenderedText] = useState('');
  const [reference, setReference] = useState('');
  /** The house is paying for this table — friends and family of the boss. */
  const [invite, setInvite] = useState(false);
  /** With invoicing on, the invite closes (and its invoice is issued) only after this question. */
  const [choosingBuyer, setChoosingBuyer] = useState(false);
  const { data: settings } = useSettings();
  /** Outside the bill: saved on the order, never charged or printed. */
  const tip = useTipInput(order.tipCents, open);

  // Split-by-item state.
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [lastSplit, setLastSplit] = useState<{ paymentId: string; amountCents: number; method: PaymentMethod } | null>(null);
  const [movingItem, setMovingItem] = useState<OrderItem | null>(null);

  const balanceDue = order.balanceDueCents;

  useEffect(() => {
    if (!open) return;
    setMode('full');
    setMethod('CREDIT_CARD');
    setAmountText(formatAmount(balanceDue, decimals));
    setTenderedText('');
    setReference('');
    setInvite(false);
    setSelections({});
    setLastSplit(null);
  }, [open, balanceDue]);

  const amountCents = useMemo(() => {
    try {
      return parseAmountToCents(amountText || '0');
    } catch {
      return NaN;
    }
  }, [amountText]);

  const tenderedCents = useMemo(() => {
    if (!tenderedText) return null;
    try {
      return parseAmountToCents(tenderedText);
    } catch {
      return NaN;
    }
  }, [tenderedText]);

  const amountValid = Number.isFinite(amountCents) && amountCents > 0 && amountCents <= balanceDue;
  const tenderValid =
    tenderedCents === null || (Number.isFinite(tenderedCents) && tenderedCents >= amountCents);

  const pay = useMutation({
    mutationFn: async () => {
      // The tip goes on first: once the last payment lands the order may close.
      if (tip.cents !== order.tipCents) await api.orders.update(order.id, { tipCents: tip.cents });
      return api.orders.pay(order.id, {
        amountCents,
        method,
        reference: reference.trim() || null,
        tenderedCents: method === 'CASH' && tenderedCents ? tenderedCents : null,
      });
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['order'] });
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      if (updated.balanceDueCents === 0) {
        toast.success('Payment complete.');
        onSettled(updated);
      } else {
        toast.success(`Part payment taken — ${money(updated.balanceDueCents)} still due.`);
        setAmountText(formatAmount(updated.balanceDueCents, decimals));
        setTenderedText('');
      }
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not take that payment.'),
  });

  /** Splittable lines: sent (or further along) and not voided. */
  const splittableItems = order.items.filter(
    (item) => item.status !== 'DRAFT' && item.status !== 'CANCELLED',
  );
  const selectionList = Object.entries(selections)
    .filter(([, quantity]) => quantity > 0)
    .map(([orderItemId, quantity]) => ({ orderItemId, quantity }));

  const { data: splitQuote } = useQuery({
    queryKey: ['split-quote', order.id, selectionList],
    queryFn: () => api.orders.splitPaymentQuote(order.id, selectionList),
    enabled: mode === 'split' && selectionList.length > 0,
  });
  const splitAmountCents = splitQuote?.amountCents ?? 0;
  const splitTenderValid =
    tenderedCents === null || (Number.isFinite(tenderedCents) && tenderedCents >= splitAmountCents);

  const splitPay = useMutation({
    mutationFn: () =>
      api.orders.splitPayment(order.id, {
        selections: selectionList,
        method,
        reference: reference.trim() || null,
        tenderedCents: method === 'CASH' && tenderedCents ? tenderedCents : null,
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['order'] });
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      const justPaid = updated.payments.at(-1) ?? null;
      setSelections({});
      setTenderedText('');
      if (updated.balanceDueCents === 0) {
        toast.success('Payment complete.');
        onSettled(updated);
      } else {
        toast.success(`Split payment taken — ${money(updated.balanceDueCents)} still due.`);
        if (justPaid) {
          setLastSplit({ paymentId: justPaid.id, amountCents: justPaid.amountCents, method: justPaid.method });
        }
      }
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not take that payment.'),
  });

  /**
   * An invited table takes no money at all, so it does not go through the
   * payment route: the order is closed straight away and flagged, which is what
   * keeps its total out of the takings in history while the bill itself stays.
   */
  const closeAsInvite = useMutation({
    mutationFn: () => api.orders.close(order.id, { isInvite: true }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['order'] });
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Closed as an invitation — nothing charged.');
      onSettled(updated);
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not close this table.'),
  });

  const chargeDisabled =
    mode === 'full'
      ? !amountValid || !tenderValid || !tip.valid
      : selectionList.length === 0 || !splitQuote || !splitTenderValid;

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="Take Payment"
      subtitle={`Table ${order.tableNumber} · Order #${order.orderNumber}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {invite ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Gift size={16} />}
              loading={closeAsInvite.isPending}
              onClick={() => (settings?.invoicingEnabled ? setChoosingBuyer(true) : closeAsInvite.mutate())}
            >
              Close as invite
            </Button>
          ) : mode === 'split' ? (
            <Button
              variant="primary"
              size="lg"
              loading={splitPay.isPending}
              disabled={chargeDisabled}
              onClick={() => splitPay.mutate()}
            >
              Charge {splitQuote ? money(splitAmountCents) : '—'}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              loading={pay.isPending}
              disabled={chargeDisabled}
              onClick={() => pay.mutate()}
            >
              Charge {Number.isFinite(amountCents) ? money(amountCents) : '—'}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-6 md:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
          {/*
            Comping a table is a decision, not a payment method, so it sits
            above the keypad rather than beside CASH and CARD: ticking it puts
            the whole bill on the house and takes the charge controls away.
          */}
          <label
            className={clsx(
              'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition',
              invite
                ? 'border-accent bg-accent/12'
                : 'border-line hover:border-line-strong',
            )}
          >
            <input
              type="checkbox"
              checked={invite}
              onChange={(event) => setInvite(event.target.checked)}
              className="mt-0.5 h-4.5 w-4.5 shrink-0 accent-accent"
            />
            <span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
                <Gift size={15} className={invite ? 'text-accent-strong' : 'text-ink-faint'} />
                Invite table — on the house
              </span>
              <span className="mt-0.5 block text-xs text-ink-faint">
                Guests of the house. The bill is kept in full but nothing is collected, and the
                total is left out of the takings in history.
              </span>
            </span>
          </label>

          {invite ? (
            <div className="rounded-card border border-accent/40 bg-accent/8 px-4 py-5 text-sm text-ink-soft">
              <p>
                Closing this table will write off{' '}
                <span className="tnum font-medium text-accent-strong">{money(balanceDue)}</span> as an
                invitation.
              </p>
              {order.paidCents > 0 ? (
                <p className="mt-2">
                  The {money(order.paidCents)} already taken stays on the order and still counts.
                </p>
              ) : null}
              <p className="mt-2 text-xs text-ink-faint">
                The order closes and the table is freed straight away — no payment is recorded.
              </p>
            </div>
          ) : (
          <>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode('full')}
                className={clsx(
                  'h-10 flex-1 rounded-lg border text-sm font-medium transition',
                  mode === 'full'
                    ? 'border-accent bg-accent/12 text-accent-strong'
                    : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                )}
              >
                Full balance
              </button>
              <button
                type="button"
                onClick={() => setMode('split')}
                className={clsx(
                  'h-10 flex-1 rounded-lg border text-sm font-medium transition',
                  mode === 'split'
                    ? 'border-accent bg-accent/12 text-accent-strong'
                    : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
                )}
              >
                Split by item
              </button>
            </div>

            {mode === 'split' ? (
              <>
                {lastSplit ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-positive/40 bg-positive/10 px-4 py-3">
                    <span className="text-sm text-ink-soft">
                      Charged <span className="tnum font-medium text-positive">{money(lastSplit.amountCents)}</span>{' '}
                      by {PAYMENT_METHOD_LABELS[lastSplit.method]}.
                    </span>
                    <SendToPrinterButton
                      orderId={order.id}
                      kind="RECEIPT"
                      paymentId={lastSplit.paymentId}
                      variant="secondary"
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={() => setLastSplit(null)}
                      className="ml-auto text-xs text-ink-faint hover:text-ink"
                    >
                      Next guest
                    </button>
                  </div>
                ) : null}

                <div>
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-faint">
                    Which items is this guest paying for?
                  </span>
                  <p className="mb-2 text-xs text-ink-faint">
                    Rather have a dish follow the guests instead of being paid here? Move it to
                    another table or bill from its row.
                  </p>
                  <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                    {splittableItems.map((item) => (
                      <SplitItemRow
                        key={item.id}
                        item={item}
                        quantity={selections[item.id] ?? 0}
                        onChange={(next) => setSelections((current) => ({ ...current, [item.id]: next }))}
                        onMove={() => setMovingItem(item)}
                      />
                    ))}
                  </div>
                </div>

                <PaymentMethodFields
                  method={method}
                  onMethod={setMethod}
                  amountCents={splitAmountCents}
                  tenderedText={tenderedText}
                  onTenderedChange={setTenderedText}
                  reference={reference}
                  onReferenceChange={setReference}
                />
              </>
            ) : (
              <>
                <Field
                  label="Amount to charge"
                  error={!amountValid && amountText ? `Must be between 0 and ${money(balanceDue)}` : undefined}
                >
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">{symbol}</span>
                    <Input
                      inputMode="decimal"
                      className="pl-8 text-lg"
                      value={amountText}
                      onChange={(event) => setAmountText(event.target.value)}
                    />
                  </div>
                </Field>

                <PaymentMethodFields
                  method={method}
                  onMethod={setMethod}
                  amountCents={amountCents || 0}
                  tenderedText={tenderedText}
                  onTenderedChange={setTenderedText}
                  reference={reference}
                  onReferenceChange={setReference}
                />

                <TipField tip={tip} />
              </>
            )}
          </>
          )}
        </div>

        <aside className="rounded-card border border-line bg-canvas p-5">
          <h3 className="mb-4 font-display text-lg text-ink">Bill</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-faint">Subtotal</dt>
              <dd className="tnum text-ink">{money(order.totals.subtotalCents)}</dd>
            </div>
            {order.totals.itemDiscountCents > 0 ? (
              <div className="flex justify-between">
                <dt className="text-ink-faint">Item discounts</dt>
                <dd className="tnum text-positive">−{money(order.totals.itemDiscountCents)}</dd>
              </div>
            ) : null}
            {order.totals.billDiscountCents > 0 ? (
              <div className="flex justify-between">
                <dt className="text-ink-faint">
                  Bill discount
                  {order.totals.discountType === 'PERCENT'
                    ? ` ${formatRate(order.totals.discountValue)}%`
                    : ''}
                </dt>
                <dd className="tnum text-positive">−{money(order.totals.billDiscountCents)}</dd>
              </div>
            ) : null}
            {order.totals.serviceChargeCents > 0 ? (
              <div className="flex justify-between">
                <dt className="text-ink-faint">
                  Service {formatRate(order.totals.serviceChargeBp)}%
                </dt>
                <dd className="tnum text-ink">{money(order.totals.serviceChargeCents)}</dd>
              </div>
            ) : null}
            {order.totals.taxBreakdown.map((bucket) => (
              <div key={`${bucket.taxType}-${bucket.rateBp}`} className="flex justify-between">
                <dt className="text-ink-faint">
                  {bucket.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food'} tax {formatRate(bucket.rateBp)}%
                </dt>
                <dd className="tnum text-ink">{money(bucket.taxCents)}</dd>
              </div>
            ))}
          </dl>

          <div className="my-4 h-px bg-line" />

          <div className="flex items-baseline justify-between">
            <span className="text-sm text-ink-faint">Total</span>
            <span className="tnum font-display text-2xl text-ink">
              {money(order.totals.grandTotalCents)}
            </span>
          </div>

          {order.paidCents > 0 ? (
            <div className="mt-2 flex items-baseline justify-between text-sm">
              <span className="text-ink-faint">Already paid</span>
              <span className="tnum text-positive">−{money(order.paidCents)}</span>
            </div>
          ) : null}

          <div className="mt-4 rounded-lg border border-accent/40 bg-accent/10 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-accent">
              {invite ? 'On the house' : 'Balance due'}
            </div>
            <div
              className={clsx(
                'tnum font-display text-3xl text-accent-strong',
                invite && 'line-through decoration-accent/60',
              )}
            >
              {money(balanceDue)}
            </div>
          </div>

          {/*
            The bill goes to the printer at the till, not to this device — a
            waiter on a phone has no route to it. It sits here rather than on the
            order screen so that printing inherits the unsent-items guard: this
            dialog is only reachable once every draft line has been sent or
            removed, so the paper can never be missing a round.
          */}
          <div className="mt-4">
            <SendToPrinterButton orderId={order.id} kind="BILL" variant="secondary" block />
            <p className="mt-2 text-xs text-ink-faint">
              Prints at the till within a couple of seconds. No one needs to confirm it there.
            </p>
          </div>
        </aside>
      </div>
    </Modal>

    <CloseOrderDialog
      order={order}
      open={choosingBuyer}
      closing={closeAsInvite.isPending}
      onClose={() => setChoosingBuyer(false)}
      onConfirm={() => {
        setChoosingBuyer(false);
        closeAsInvite.mutate();
      }}
    />

    <MoveItemDialog
      order={order}
      preselect={movingItem}
      open={movingItem !== null}
      onClose={() => setMovingItem(null)}
      onMoved={() => void queryClient.invalidateQueries({ queryKey: ['order', order.id] })}
    />
    </>
  );
}
